import { useEffect, useRef } from 'react';

type RippleGroundProps = {
  className?: string;
};

// Cadence and fade mirror docs/redesign/reference-identity-study.html's
// `#ripple` script: 9 concentric rings, drifting outward from an off-canvas
// top-right origin, fading out well before the far edge.
const RING_COUNT = 9;
const RING_SPACING = 60;
const DRIFT_PX_PER_MS = 0.012;
// Kept deliberately faint — this is ambient texture, not a focal element.
// The static (reduced-motion) frame is dialed even fainter than the
// animated loop, matching the reference's `@media (prefers-reduced-motion)`
// opacity step-down.
const MAX_ALPHA = 0.14;
const MAX_ALPHA_STATIC = 0.1;

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const readPrimaryColor = () =>
  getComputedStyle(document.documentElement).getPropertyValue('--color-primary').trim();

/**
 * Ambient hero texture — slow, faint concentric rings drawn from an
 * off-canvas top-right origin, doubling as fingerprint ridges and water
 * ripples (see the reference identity study's `#ripple` canvas). Purely
 * decorative: absolutely positioned, `pointer-events-none`, `aria-hidden`,
 * DPR-aware, and cleans up its rAF loop + resize listener on unmount.
 *
 * Under `prefers-reduced-motion: reduce`, draws a single static faint frame
 * instead of animating. Mount inside a `relative overflow-hidden` container,
 * behind the actual content (e.g. the hero's text stack).
 */
export const RippleGround = ({ className }: RippleGroundProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const reduce = prefersReducedMotion();
    let width = 0;
    let height = 0;

    // Declared before `resize` because the initial `resize()` call below invokes
    // `draw` in the reduced-motion branch — a `const` referenced before its
    // declaration would throw a temporal-dead-zone ReferenceError and blank the
    // page for reduced-motion users.
    const draw = (time: number, maxAlpha: number) => {
      const color = readPrimaryColor();
      if (!color) return;

      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      ctx.clearRect(0, 0, width, height);
      const cx = width * 0.82;
      const cy = height * 0.16;
      const base = (time * DRIFT_PX_PER_MS) % RING_SPACING;
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.2 * dpr;
      for (let i = 0; i < RING_COUNT; i++) {
        const radius = (base + i * RING_SPACING) * dpr;
        if (radius <= 0) continue;
        ctx.globalAlpha = Math.max(0, maxAlpha * (1 - radius / (Math.max(width, height) * 0.95)));
        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.stroke();
      }
    };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = canvas.getBoundingClientRect();
      width = canvas.width = rect.width * dpr;
      height = canvas.height = rect.height * dpr;
      if (reduce) {
        draw(3000, MAX_ALPHA_STATIC);
      }
    };
    resize();
    window.addEventListener('resize', resize);

    let raf = 0;
    if (reduce) {
      draw(3000, MAX_ALPHA_STATIC);
    } else {
      const loop = (time: number) => {
        draw(time, MAX_ALPHA);
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
    }

    return () => {
      window.removeEventListener('resize', resize);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 z-0 h-full w-full ${className ?? ''}`}
    />
  );
};
